import React from 'react';

import { getAdMediums, type IdAdMediumDto } from '@/services/ws-consulting/src/main/resources/rest-api';
import { useSubscribe } from '@/js/libs/observable';
import { useRequest } from '@/js/libs/use-request';

import { DEFAULT_DEBOUNCE_DELAY, DROP_DOWN_ENTITY_LIMIT } from 'src/constants';
import type { Option } from 'src/types';

type UseConsultingAdMediumOptionsReturn = {
  options: Option<IdAdMediumDto>[];
  pending: boolean;
  total: number;
  searchFilter: string;
  setSearchFilter: (filter: string) => void;
};

type UseConsultingAdMediumOptions = () => UseConsultingAdMediumOptionsReturn;

export const useConsultingAdMediumOptions: UseConsultingAdMediumOptions = () => {
  const [searchFilter, setSearchFilter] = React.useState<string>('');

  const getAdMediumsRequest = useRequest({
    apiMethod: getAdMediums,
    params: { filter: searchFilter, includeInactive: true, limit: DROP_DOWN_ENTITY_LIMIT, page: 0 },
    debounce: DEFAULT_DEBOUNCE_DELAY,
  });

  const options = useSubscribe(
    getAdMediumsRequest.data$,
    (response) =>
      response?.data.items.map((item) => ({
        label: item.name,
        value: item.id,
        active: item.active,
      })) ?? [],
  );
  const total = useSubscribe(getAdMediumsRequest.data$, (result) => result?.data?.total ?? 0);
  const pending = useSubscribe(getAdMediumsRequest.pending$);

  return React.useMemo<UseConsultingAdMediumOptionsReturn>(
    () => ({
      options,
      pending,
      total,
      searchFilter,
      setSearchFilter,
    }),
    [options, pending, total, searchFilter],
  );
};
